import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { toast } from 'react-toastify'
import { CircularProgress } from '@material-ui/core'
import { useMediaQuery } from '@material-ui/core'

import GameItem from './components/game-item'
import Button from '../../components/UI/buttons/buttons-login_register/button/Button'
import { isAuthenticated } from '../../helpers'

import {
  useUserData,
  useUserParticipation,
  useTournamentNavigation,
} from '../../components/cup-top/hooks'
import { useTournamentValidation, useTournamentDisplay } from './hooks'

import styles from './styles.module.scss'

export default function CupInformation({ tournament }) {
  const { id } = useParams()
  const isSmallMobile = useMediaQuery('(max-width: 480px)')

  const { userData, requestSuccess } = useUserData()
  const participation = useUserParticipation(
    tournament,
    userData,
    requestSuccess
  )
  const validation = useTournamentValidation(tournament, userData)
  const display = useTournamentDisplay(tournament)
  const navigation = useTournamentNavigation(tournament, id)

  const showErrorToast = useCallback(() => {
    toast.error(validation.errorMessage)
  }, [validation.errorMessage])

  const handleRegistrationClick = useCallback(() => {
    participation.checkParticipationTypeData(
      tournament?.participationType,
      navigation.goToUrl
    )
  }, [participation, tournament?.participationType, navigation.goToUrl])

  const handleAddGameClick = useCallback(() => {
    navigation.goToUrl('/games')
  }, [navigation.goToUrl])

  const handleLoginClick = useCallback(() => {
    navigation.goToUrl('/login')
  }, [navigation.goToUrl])

  const handleProfileClick = useCallback(() => {
    navigation.goToUrl('/games')
  }, [navigation.goToUrl])

  return (
    <div className={styles.wrapper} data-testid='cupInformation'>
      {/* Registration Period */}
      {display.registrationPeriod && (
        <div className={classNames(styles.infoCard, styles.primary)}>
          <div className={styles.cardHeader}>
            <span className={styles.icon}>📅</span>
            <h2 className={styles.cardTitle}>
              <FormattedMessage id='battlecup.information.registrationPeriod' />
            </h2>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.dateRange}>
              {display.registrationPeriod.displayText}
            </div>
          </div>
        </div>
      )}

      {/* Tournament Details */}
      <div className={styles.infoCard}>
        <div className={styles.cardHeader}>
          <span className={styles.icon}>🏆</span>
          <h2 className={styles.cardTitle}>
            <FormattedMessage id='battlecup.information.tournamentDetails' />
          </h2>
        </div>
        <div className={styles.cardContent}>
          <p className={styles.description}>
            {display.tournamentDescription ? (
              display.tournamentDescription
            ) : (
              <>
                <FormattedMessage id='battlecup.information.welcome' />
                <br />
                <FormattedMessage id='battlecup.information.hostedBy' />
              </>
            )}
          </p>
          {display.platformData?.hasPlatforms && (
            <div className={styles.platformContainer}>
              <span className={styles.platformLabel}>
                <FormattedMessage id='battlecup.information.platform' />
              </span>
              <div className={styles.platformTags}>
                {display.platformData.platforms.map((el, i) => (
                  <span key={i} className={styles.platformTag}>
                    {el}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Organizer */}
      {display.organizerData && (
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <span className={styles.icon}>👨‍💼</span>
            <h2 className={styles.cardTitle}>{display.organizerData.name}</h2>
          </div>
          {display.organizerData.hasLogo && (
            <img
              className={styles.organizerLogo}
              src={display.organizerData.logo}
              alt='organizer_logo'
            />
          )}
        </div>
      )}

      {/* Prize Fund */}
      <div className={classNames(styles.infoCard, styles.prize)}>
        <div className={styles.cardHeader}>
          <span className={styles.icon}>💰</span>
          <h2 className={styles.cardTitle}>
            <FormattedMessage id='battlecup.information.prizeDetails' />
          </h2>
        </div>
        <div className={styles.cardContent}>
          {display.prizeData.hasPrize ? (
            <div className={styles.prizeAmount}>{display.prizeData.amount}</div>
          ) : (
            <FormattedMessage id='battlecup.information.noPrize' />
          )}
        </div>
      </div>

      {/* Game and Format */}
      <div className={styles.infoCard}>
        <div className={styles.cardHeader}>
          <span className={styles.icon}>🎮</span>
          <h2 className={styles.cardTitle}>
            <FormattedMessage id='battlecup.information.gameAndFormat' />
          </h2>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.gameSection}>
            {display.gameData && (
              <GameItem
                logo={display.gameData.logo}
                title={display.gameData.title}
                size={display.gameData.size}
              />
            )}
          </div>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <FormattedMessage id='battlecup.information.participantsLimit' />
              </div>
              <div className={styles.infoValue}>
                {display.tournamentStats.participantLimit}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <FormattedMessage id='battlecup.information.members.count' />
              </div>
              <div className={styles.infoValue}>
                {display.tournamentStats.membersCount}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <FormattedMessage id='battlecup.information.type' />
              </div>
              <div className={styles.infoValue}>
                {display.tournamentStats.systemType === 'doubleElimination' ? (
                  <FormattedMessage id='battlecup.information.type.doubleElimination' />
                ) : display.tournamentStats.systemType === 'FFA' ? (
                  <FormattedMessage id='battlecup.information.type.FFA' />
                ) : display.tournamentStats.systemType ===
                  'singleElimination' ? (
                  <FormattedMessage id='battlecup.information.type.singleElimination' />
                ) : display.tournamentStats.systemType === 'roundRobin' ? (
                  <FormattedMessage id='battlecup.information.type.roundRobin' />
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contacts */}
      {display.contactsData?.hasContacts && (
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <span className={styles.icon}>📞</span>
            <h2 className={styles.cardTitle}>
              <FormattedMessage id='battlecup.information.contacts' />
            </h2>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.contactList}>
              {display.contactsData.contacts.map((el, index) => (
                <div key={index} className={styles.contactItem}>
                  <span className={styles.contactName}>{el?.name}</span>
                  <a
                    href={el?.link || '#'}
                    className={styles.contactLink}
                    target={el?.link ? '_blank' : undefined}
                    rel={el?.link ? 'noopener noreferrer' : undefined}
                  >
                    {el?.title}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Social Networks */}
      {display.socialNetworksData?.hasAny && (
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <span className={styles.icon}>🌐</span>
            <h2 className={styles.cardTitle}>
              <FormattedMessage id='battlecup.information.social' />
            </h2>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.socialGrid}>
              {display.socialNetworksData.discord && (
                <a
                  href={display.socialNetworksData.discord}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classNames(styles.socialLink, styles.discord)}
                  aria-label='Join Discord'
                >
                  <span className={styles.socialIcon}>💬</span>
                  {!isSmallMobile && 'Discord'}
                </a>
              )}
              {display.socialNetworksData.twitter && (
                <a
                  href={display.socialNetworksData.twitter}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classNames(styles.socialLink, styles.twitter)}
                  aria-label='Follow on Twitter'
                >
                  <span className={styles.socialIcon}>🐦</span>
                  {!isSmallMobile && 'Twitter'}
                </a>
              )}
              {display.socialNetworksData.facebook && (
                <a
                  href={display.socialNetworksData.facebook}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classNames(styles.socialLink, styles.facebook)}
                  aria-label='Follow on Facebook'
                >
                  <span className={styles.socialIcon}>📘</span>
                  {!isSmallMobile && 'Facebook'}
                </a>
              )}
              {display.socialNetworksData.instagram && (
                <a
                  href={display.socialNetworksData.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classNames(styles.socialLink, styles.instagram)}
                  aria-label='Follow on Instagram'
                >
                  <span className={styles.socialIcon}>📷</span>
                  {!isSmallMobile && 'Instagram'}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Partners, Sponsors, Organizers */}
      {display.mediaData?.hasAnyMedia && (
        <div className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <span className={styles.icon}>🤝</span>
            <h2 className={styles.cardTitle}>Partners & Sponsors</h2>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.logoGrid}>
              {display.mediaData.partnerLogos.map((el, index) => (
                <div key={`partner-${index}`} className={styles.logoItem}>
                  <img
                    className={styles.partnerLogo}
                    src={el}
                    alt='partner_logo'
                  />
                </div>
              ))}
              {display.mediaData.sponsorLogos.map((el, index) => (
                <div key={`sponsor-${index}`} className={styles.logoItem}>
                  <img
                    className={styles.sponsorLogo}
                    src={el}
                    alt='sponsor_logo'
                  />
                </div>
              ))}
              {display.mediaData.organizerLogos.map((el, index) => (
                <div key={`organizer-${index}`} className={styles.logoItem}>
                  <img
                    className={styles.organizerLogo}
                    src={el}
                    alt='organizer_logo'
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Registration Button */}
      <div className={styles.registrationSection}>
        {isAuthenticated() ? (
          requestSuccess ? (
            participation.isUserRegistered ? (
              <Button color='yellow' type='cupbottom'>
                <FormattedMessage id='battlecup.information.already.reg' />
              </Button>
            ) : participation.canRegister ? (
              participation.shouldShowAddGameButton ? (
                <Button
                  active
                  color='red'
                  type='cupbottom'
                  onClick={handleAddGameClick}
                >
                  <FormattedMessage id='settings.games.addGame' />
                </Button>
              ) : participation.shouldShowRegistrationButton ? (
                validation.errorMessage ? (
                  <Button
                    active
                    color='yellow'
                    type='cupbottom'
                    onClick={showErrorToast}
                  >
                    <FormattedMessage id='battlecup.information.registrationButton' />{' '}
                    *
                  </Button>
                ) : (
                  <Button
                    active
                    color='yellow'
                    type='cupbottom'
                    onClick={handleRegistrationClick}
                  >
                    <FormattedMessage id='battlecup.information.registrationButton' />
                  </Button>
                )
              ) : participation.shouldShowFillProfileButton ? (
                <Button
                  active
                  color='red'
                  type='cupbottom'
                  onClick={handleProfileClick}
                >
                  <FormattedMessage id='battlecup.information.fillProfile' />
                </Button>
              ) : null
            ) : null
          ) : (
            <CircularProgress />
          )
        ) : (
          <Button
            active
            color='red'
            type='cupbottom'
            onClick={handleLoginClick}
          >
            <FormattedMessage id='navigation.login' />
          </Button>
        )}
      </div>
    </div>
  )
}
